<template>
    <div class="ls-flow--container-with-select">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div class="ls-flow--select">
            <LsSelect
                v-model="selectedDispute"
                :options="options"
                name="dispute"
                track-id="past_disputes_view-dispute_options"
            />
        </div>
        <div v-if="!loading" class="ls-flow--container" test-id="main-view">
            <h2>Disputed Job:</h2>
            <h2>{{ selectedDispute.schedule_event.status_set_at | timestamp }}</h2>
            <div
                v-for="disputeItem in disputeData"
                :key="disputeItem.title"
                class="ls-past-disputes-view--dispute-data"
            >
                <span>{{ disputeItem.title }}</span>
                <span>{{ disputeItem.value }}</span>
            </div>
            <div class="ls-past-disputes-view--dispute-images-container">
                <span>Customer photos:</span>
                <div v-if="selectedDispute.images.customer.length">
                    <LsImageRow
                        :images="selectedDispute.images.customer"
                        class="ls-past-disputes-view--dispute-images"
                    />
                </div>
                <span v-else>N/A</span>
            </div>
            <div class="ls-past-disputes-view--dispute-images-container">
                <span>Pro photos:</span>
                <div v-if="selectedDispute.images.contractor.length" test-id="pro-photos">
                    <LsImageRow
                        :images="selectedDispute.images.contractor"
                        class="ls-past-disputes-view--dispute-images"
                    />
                </div>
                <span v-else>N/A</span>
            </div>
            <PastDisputesViewFooter
                :selected-dispute="selectedDispute"
                :services="allRecentAndCurrentServices"
            />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsFilters } from 'ls-vue-common';
import { lsHelpers } from '@/helpers';
import { LsSelect } from '@/components/common';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import LsImageRow from '@/components/LsImageRow/LsImageRow.vue';
import PastDisputesViewFooter from './PastDisputesViewFooter.vue';
import DisputeCompletions from '@/graphql/queries/DisputeCompletions.gql';
import {
    disputeStatusLabel,
    disputeReasonOptions,
    disputeReasonDetailsOptions,
    evidenceRouteLabel,
    customerContestAnswerLabel,
} from './PastDisputes.constants';
import AllRecentAndCurrentServices from '@/graphql/queries/AllRecentAndCurrentServices.gql';
import DEFAULT_ALL_RECENT_AND_CURRENT_SERVICES from '@/graphql/queries/AllRecentAndCurrentServices.defaults';
import { apolloClient } from '@/vue-apollo';

export default {
    name: 'PastDisputesView',
    components: { LsTemplatedLoading, LsSelect, LsImageRow, PastDisputesViewFooter },
    data() {
        let allRecentAndCurrentServices = DEFAULT_ALL_RECENT_AND_CURRENT_SERVICES;
        try {
            const result = apolloClient.readQuery({
                query: AllRecentAndCurrentServices,
                variables: {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                },
            });
            if (result && result.allRecentAndCurrentServices) {
                allRecentAndCurrentServices = result.allRecentAndCurrentServices;
            }
        } catch (e) {
            // Cache miss on deep-link; the parent query populates it.
        }

        return {
            options: [],
            selectedDispute: null,
            loading: 0,
            allRecentAndCurrentServices: allRecentAndCurrentServices.schedules || [],
        };
    },
    computed: {
        generateDisputeTypeString() {
            const disputeTypeStringArray = _.split(this.selectedDispute.dispute_type, '_');

            return _.map(disputeTypeStringArray, (word) => {
                return _.capitalize(word);
            }).join(' ');
        },
        disputeData() {
            if (this.loading) {
                return;
            }
            const { selectedDispute } = this;
            const { parseTimestamp, getLocationsString } = lsHelpers;
            const { fullName } = lsFilters;

            return [
                {
                    title: 'Dispute status:',
                    value: disputeStatusLabel(selectedDispute.dispute_status),
                },
                {
                    title: 'Dispute type:',
                    value: this.generateDisputeTypeString,
                },
                {
                    title: 'Reason:',
                    value:
                        disputeReasonOptions[selectedDispute.reason] ||
                        disputeReasonOptions[selectedDispute.dispute_type] ||
                        this.selectedDispute.reason,
                },
                {
                    title: 'Reason details:',
                    value:
                        disputeReasonDetailsOptions[selectedDispute.reason_details] ||
                        this.selectedDispute.reason,
                },
                {
                    title: 'Dispute created by:',
                    value: `${fullName({
                        user: selectedDispute.creating_user,
                    })} ${selectedDispute.creating_user.is_admin ? '(Admin)' : '(Customer)'}`,
                },
                {
                    title: 'Requested by:',
                    value: `${fullName({
                        user: selectedDispute.customer.user,
                    })} (Customer)`,
                },
                {
                    title: 'Requested on:',
                    value: parseTimestamp({ timestamp: selectedDispute.created_at }),
                },
                {
                    title: 'Job marked complete:',
                    value: parseTimestamp({
                        timestamp: this.selectedDispute.schedule_event.status_set_at,
                    }),
                },
                {
                    title: 'GPS check-in:',
                    value: _.get(selectedDispute, 'geofence.entered_at')
                        ? parseTimestamp({ timestamp: selectedDispute.geofence.entered_at })
                        : 'N/A',
                },
                {
                    title: 'Evidence:',
                    value: evidenceRouteLabel[selectedDispute.evidence_route] || 'N/A',
                },
                {
                    title: 'Customer answer:',
                    value: customerContestAnswerLabel(selectedDispute),
                },
                {
                    title: 'Expires:',
                    value: parseTimestamp({ timestamp: selectedDispute.expires_at }),
                },
                {
                    title: 'Locations:',
                    value: getLocationsString({ locations: selectedDispute }),
                },
                {
                    title: 'Customer message:',
                    value: _.get(selectedDispute.comments, '[0].text') || 'N/A',
                },
            ];
        },
    },
    apollo: {
        disputeCompletions: {
            query: DisputeCompletions,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                    schedule_id: _.toNumber(this.$route.params.schedule_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.options = _.map(data.disputeCompletions, (dispute) => {
                        return {
                            label: `Service date: ${lsHelpers.parseTimestamp({
                                timestamp: dispute.schedule_event.contractor_starts_at,
                            })}`,
                            value: dispute,
                        };
                    });

                    const dispute_id = _.get(this.$route.query, 'dispute_id');

                    if (dispute_id) {
                        const validDispute = _.find(data.disputeCompletions, (dispute) => {
                            return dispute.id === dispute_id;
                        });

                        if (validDispute) {
                            this.selectedDispute = validDispute;
                            return;
                        }
                        lsHelpers.goToParentView({ replace: true });
                    }

                    this.selectedDispute = data.disputeCompletions[0];
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
            },
            fetchPolicy: 'network-only',
            loadingKey: 'loading',
        },
    },
};
</script>

<style lang="scss" scoped>
h2 {
    font-size: 1.9rem;
    color: $primary;

    &:last-of-type {
        padding-bottom: 20px;
    }
}
.ls-past-disputes-view--dispute-data {
    padding: 7px 0;

    span:first-child {
        font-weight: $semi-bold;
        padding-right: 5px;
    }
}

.ls-past-disputes-view--dispute-images-container {
    margin: 7px 0;

    span:first-child {
        font-weight: $semi-bold;
        padding-right: 5px;
    }

    .ls-past-disputes-view--dispute-images {
        margin-top: 14px;
    }
}
</style>
